/**
 * What a resolved mesh draws: its geometry and each draw group's own material, whatever kind of
 * mesh it is and wherever it lives. MeshInstance3D and GridMap read a mesh through this one hook.
 * An ArrayMesh with a path decodes once through the ArrayMesh processor. Every other mesh builds in
 * place. A TextMesh builds once its font has loaded.
 */
import { useMemo } from 'react';
import { warn } from '../../logger';
import { projectPrimitiveMeshTexelSize } from '../../parser/projectSettingsParser';
import { decodeSceneArrayMesh } from '../../resources/meshes/arraymesh/decode';
import { godotSurfaceParts } from '../../resources/meshes/godotSurface';
import { meshKind, primitiveMaterialAddress, undrawnMeshType, } from '../../resources/meshes/meshSource';
import { useResolvableMesh } from '../../resources/meshes/useResolvableMesh';
import { useResource } from '../../resources/useResource';
import { NODE_PROPERTY } from '../../resources/useTexture2D';
import { useProjectSettings } from '../contexts/ProjectSettingsContext';
import { fileMaterialSources, resolveMaterialSource } from '../materials/materialSource';
import { useDisposeOnRelease } from '../useDisposeOnRelease';
import { useUndrawnMeshReport } from '../contexts/UndrawnMeshesContext';
import { primitiveMeshGeometry } from './primitiveMeshGeometry';
import { isLocalToScene } from '../../resources/textures/viewporttexture/localToScene.js';
/** `items` as a list known to hold one, or null for none. */
function nonEmpty(items) {
    const [first, ...rest] = items;
    return first === undefined ? null : [first, ...rest];
}
/** The R3F tag that draws a surface: `THREE.Points` for geometry of Godot's `PRIMITIVE_POINTS`, else a `THREE.Mesh`. */
export function drawnTag(drawsPoints) {
    return drawsPoints ? 'points' : 'mesh';
}
const UNRESOLVED = { kind: 'unresolved' };
const NONE = { kind: 'none' };
const LOADING = { kind: 'loading' };
const EMPTY = { kind: 'empty' };
/** The path the ArrayMesh processor decodes, for an ArrayMesh with a path. Null for any other mesh. */
export function arrayMeshPath({ scoped, resourcePath }) {
    return resourcePath && scoped && meshKind(scoped.resource.type) === 'arrayMesh' ? resourcePath : null;
}
/**
 * `isHeldLocally` is false when a resource between the node and the mesh, such as a GridMap's
 * MeshLibrary, is not local to scene. A mesh that is a node's property has none between.
 *
 * @canonical Mesh content and its disposal
 */
export function useMeshContent(resolution, isHeldLocally = NODE_PROPERTY) {
    const { scoped, status, resourcePath } = resolution;
    const kind = scoped ? meshKind(scoped.resource.type) : null;
    const isMaterialHeldLocally = scoped ? isHeldLocally && isLocalToScene(scoped.resource) : isHeldLocally;
    const processorPath = arrayMeshPath(resolution);
    // `''` requests nothing.
    const fileArrayMesh = useResource(processorPath ?? '', 'arraymesh');
    const fileSurfaced = useMemo(() => fileArrayMesh.value && withFileMaterials(fileArrayMesh.value, isMaterialHeldLocally), [fileArrayMesh.value, isMaterialHeldLocally]);
    const sceneSurfaced = useSceneArrayMesh(kind === 'arrayMesh' && !processorPath ? scoped : null, isMaterialHeldLocally);
    const { mesh: resolvable, isLoading: isLoadingReferences } = useResolvableMesh(kind === 'primitive' ? scoped : null);
    const undrawnType = scoped ? undrawnMeshType(scoped, resolvable) : null;
    useUndrawnMeshReport(undrawnType);
    const primitiveSurfaced = usePrimitiveMesh(useMemo(() => (resolvable ? { ...resolvable, path: resourcePath } : null), [resolvable, resourcePath]), isMaterialHeldLocally);
    if (resolution.isNone)
        return NONE;
    if (status === 'pending')
        return LOADING;
    if (!scoped || !kind)
        return UNRESOLVED;
    if (kind === 'surfaceless')
        return EMPTY;
    if (kind === 'primitive') {
        if (isLoadingReferences)
            return LOADING;
        if (undrawnType)
            return UNRESOLVED;
        return primitiveSurfaced ? { kind: 'surfaced', mesh: primitiveSurfaced } : EMPTY;
    }
    if (!processorPath)
        return sceneSurfaced ? { kind: 'surfaced', mesh: sceneSurfaced } : UNRESOLVED;
    if (fileArrayMesh.status === 'unavailable')
        return UNRESOLVED;
    if (!fileArrayMesh.value)
        return LOADING;
    return fileSurfaced ? { kind: 'surfaced', mesh: fileSurfaced } : EMPTY;
}
/**
 * An ArrayMesh the processor decoded, each material a file the loader fetches, held as `isHeldLocally` says.
 * Null for one with no surface.
 */
function withFileMaterials(mesh, isHeldLocally) {
    const parts = nonEmpty(mesh.parts.map(({ geometry, surfaceIndices, materialPaths }) => ({
        geometry,
        surfaceIndices,
        materials: fileMaterialSources(materialPaths, isHeldLocally),
    })));
    return parts && { parts };
}
/**
 * A PrimitiveMesh's one surface under its own material, held as `isHeldLocally` says, or null for a mesh Godot
 * builds no surface for.
 */
function usePrimitiveMesh(source, isHeldLocally) {
    const geometry = usePrimitiveMeshGeometry(source);
    const material = useMemo(() => source
        ? resolveMaterialSource(primitiveMaterialAddress(source, source.path), source.resources, isHeldLocally)
        : undefined, [source, isHeldLocally]);
    return useMemo(() => (geometry && source ? { parts: [{ geometry, surfaceIndices: [0], materials: [material] }] } : null), [geometry, material, source]);
}
/** A PrimitiveMesh's geometry at the project's texel size, or null for none or a mesh with no surface. */
function usePrimitiveMeshGeometry(source) {
    const texelSize = projectPrimitiveMeshTexelSize(useProjectSettings().settings);
    const geometry = useMemo(() => (source ? primitiveMeshGeometry(source, texelSize) : null), [source, texelSize]);
    useDisposeOnRelease(geometry);
    return geometry;
}
/**
 * An ArrayMesh in the scene's tables, with each surface's material resolved against the tables
 * that hold the mesh and held as `isHeldLocally` says. Null for no mesh or for unreadable surfaces.
 */
function useSceneArrayMesh(mesh, isHeldLocally) {
    const decoded = useSceneArrayMeshGeometry(mesh?.resource);
    const resources = mesh?.resources;
    // Apart from the decode: an edit to a material's body or to an `ext_resource`
    // path changes the resources and leaves the surface bytes alone.
    return useMemo(() => {
        if (!decoded || !resources)
            return null;
        const parts = nonEmpty(decoded.parts.map(({ geometry, surfaceIndices, materialRefs }) => ({
            geometry,
            surfaceIndices,
            materials: materialRefs.map((ref) => resolveMaterialSource(ref, resources, isHeldLocally)),
        })));
        return parts && { parts };
    }, [decoded, resources, isHeldLocally]);
}
/** The decoded geometry of a scene ArrayMesh and each surface's raw material reference. */
function useSceneArrayMeshGeometry(resource) {
    // Keyed on the surface bytes, not identity: every keystroke re-parses the scene into
    // fresh objects, and no processor cache stands in front of an inline mesh, so identity
    // deps would re-decode and re-upload it per character.
    const surfacesRaw = resource?.data['_surfaces'];
    const key = typeof surfacesRaw === 'string' ? surfacesRaw : null;
    const decoded = useMemo(() => {
        if (!resource || key === null)
            return null;
        try {
            const mesh = decodeSceneArrayMesh(resource);
            if (mesh.surfaces.length === 0)
                return null;
            const parts = godotSurfaceParts(mesh.surfaces).map(({ geometry, surfaces }) => ({
                geometry,
                surfaceIndices: surfaces.map((surface) => surface.surfaceIndex),
                materialRefs: surfaces.map((surface) => surface.materialRef),
            }));
            return { parts, geometries: { dispose: () => parts.forEach((part) => part.geometry.dispose()) } };
        }
        catch (error) {
            warn(`[mesh] scene ArrayMesh "${resource.id}" could not be decoded: ` +
                `${error instanceof Error ? error.message : String(error)}`);
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` IS the content of `resource`.
    }, [key]);
    useDisposeOnRelease(decoded?.geometries);
    return decoded;
}
//# sourceMappingURL=meshContent.js.map